<template>
  <div class="game-container">
    <div class="game-title" @click="router.go(-1)">GAME CENTER</div>
    <div class="game-detail">
      <div class="image-list">
        <div v-for="image in record.images" :key="image">
          <img class="image" :src="image" @click="bigImage = image" />
        </div>
      </div>
      <div class="big-image-container">
        <img class="big-image" :src="bigImage" />
      </div>
      <div class="game-text-container">
        <div class="text">
          <div class="title">{{ record.name }}</div>
          <div class="type">分类：{{ record.type }}</div>
          <div class="labels">
            <el-tag
              type="primary"
              v-for="label in record.labels"
              :key="label"
              class="label"
              size="large"
            >
              {{ label }}
            </el-tag>
          </div>
          <div class="description">介绍：{{ record.description }}</div>
        </div>
        <el-button class="button" :loading="playBooting" @click="handlePlay">
          PLAY
        </el-button>
      </div>
    </div>

    <el-dialog
      v-model="showFrame"
      append-to-body
      align-center
      destroy-on-close
      :close-on-click-modal="false"
      :show-close="true"
      width="min(1080px, 96vw)"
      class="game-play-dialog"
      @closed="handlePlayDialogClosed"
    >
      <template #header>
        <div class="dialog-game-header">
          <span class="dialog-game-title">正在游玩：{{ record.name }}</span>
          <div class="dialog-game-actions">
            <el-button size="small" @click="openInNewWindow">
              新窗口打开
            </el-button>
          </div>
        </div>
      </template>
      <div v-loading="frameBooting" class="play-frame-shell">
        <iframe
          v-if="showFrame"
          ref="iframeRef"
          class="play-frame"
          :src="playUrl"
          @load="handleFrameLoad"
          allowfullscreen
          referrerpolicy="strict-origin-when-cross-origin"
        />
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ElMessage } from "element-plus";
import { computed, onBeforeUnmount, onMounted, ref, shallowRef } from "vue";
import { useRoute, useRouter } from "vue-router";
import { getDetail } from "@/api/game";
import { getToken } from "@/utils/cookie.js";
import {
  getLeaderboard,
  getWallet,
  settleMatch,
  startMatch,
} from "@/api/gameCore";

const record = ref({});
const bigImage = ref("");
const playUrl = ref("");
const showFrame = ref(false);
const iframeRef = ref(null);
const currentMatchId = ref("");
const iframeReady = ref(false);
const iframeLoaded = ref(false);
const initDelivered = ref(false);
const playBooting = ref(false);
/** 勿用 depth ref，否则 payload 会变成 Proxy，postMessage 结构化克隆报错 */
const pendingInitPayload = shallowRef(null);
const router = useRouter();
const route = useRoute();

/** 弹窗内 iframe 加载 / INIT 下发完成前显示 loading */
const frameBooting = computed(
  () =>
    showFrame.value &&
    (!iframeLoaded.value || !iframeReady.value || !initDelivered.value)
);

function buildPlayUrl(raw, { embed = false } = {}) {
  if (!raw) return "";
  try {
    const url = new URL(raw, window.location.href);
    if (embed) {
      url.searchParams.set("embed", "1");
      url.searchParams.set("universe", "1");
    }
    return url.toString();
  } catch {
    if (!embed) return raw;
    const sep = raw.includes("?") ? "&" : "?";
    return `${raw}${sep}embed=1&universe=1`;
  }
}

function resetPlaySession() {
  iframeReady.value = false;
  iframeLoaded.value = false;
  initDelivered.value = false;
  pendingInitPayload.value = null;
}

function ensureLoggedIn() {
  if (getToken()) {
    return true;
  }
  ElMessage.warning("请先登录后再开始游戏");
  try {
    localStorage.setItem("wantToJump", route.fullPath);
  } catch {
    /* ignore */
  }
  router.push("/login");
  return false;
}

onMounted(async () => {
  const res = await getDetail(route.params.id);
  if (res && res.code === 200) {
    record.value = res.data;
    bigImage.value = res.data.images?.[0] || res.data.coverImage || "";
  }
});

const handlePlay = async () => {
  if (!ensureLoggedIn()) {
    return;
  }
  const mode = (record.value.playMode || "iframe").toLowerCase();
  const rawUrl = record.value.embedUrl || record.value.targetUrl || "";
  if (!rawUrl) {
    ElMessage.warning("游戏地址未配置，请联系管理员");
    return;
  }
  if (mode !== "iframe") {
    playUrl.value = buildPlayUrl(rawUrl, { embed: false });
    openInNewWindow();
    return;
  }

  resetPlaySession();
  playBooting.value = true;
  try {
    // 先并行拉开局/钱包，再开弹窗，减少 iframe 内空等
    pendingInitPayload.value = await buildInitPayload();
    playUrl.value = buildPlayUrl(rawUrl, { embed: true });
    showFrame.value = true;
  } finally {
    playBooting.value = false;
  }
};

const openInNewWindow = () => {
  if (!ensureLoggedIn()) {
    return;
  }
  const rawUrl = record.value.embedUrl || record.value.targetUrl || "";
  if (!rawUrl) {
    ElMessage.warning("游戏地址未配置，请联系管理员");
    return;
  }
  window.open(buildPlayUrl(rawUrl, { embed: false }), "_blank");
};

function cloneForPostMessage(obj) {
  try {
    return JSON.parse(JSON.stringify(obj));
  } catch (e) {
    console.error("cloneForPostMessage failed", e);
    return obj;
  }
}

const postToGameFrame = (payload) => {
  if (!iframeRef.value || !iframeRef.value.contentWindow) return;
  iframeRef.value.contentWindow.postMessage(cloneForPostMessage(payload), "*");
};

/** 子页面脚本就绪且 iframe 已 load 后再下发 INIT；同一会话只发一次，避免重复 reset */
function flushInitToGame(force = false) {
  if (!pendingInitPayload.value || !iframeRef.value?.contentWindow) return;
  if (!iframeLoaded.value || !iframeReady.value) return;
  if (initDelivered.value && !force) return;

  initDelivered.value = true;
  postToGameFrame({
    type: "UNIVERSE_GAME_INIT",
    payload: pendingInitPayload.value,
  });
}

async function buildInitPayload() {
  const gameId = record.value.id;
  const [matchRes, walletRes] = await Promise.all([
    startMatch({ gameId }).catch(() => null),
    getWallet({ currencyCode: "GOLD" }).catch(() => null),
  ]);

  const matchId = matchRes?.data?.matchId || "";
  currentMatchId.value = matchId;

  return {
    gameId,
    matchId: currentMatchId.value,
    walletBalance: walletRes?.data?.balance ?? 0,
    currencyCode: walletRes?.data?.currencyCode || "GOLD",
  };
}

async function refreshInitPayload() {
  if (!record.value?.id) return;
  pendingInitPayload.value = await buildInitPayload();
  initDelivered.value = false;
  flushInitToGame(true);
}

const handleFrameLoad = async () => {
  if (!showFrame.value || !record.value.id) return;
  iframeLoaded.value = true;
  if (!pendingInitPayload.value) {
    pendingInitPayload.value = await buildInitPayload();
  }
  flushInitToGame();
};

function handlePlayDialogClosed() {
  resetPlaySession();
}

const onGameMessage = async (event) => {
  if (!iframeRef.value || event.source !== iframeRef.value.contentWindow) {
    return;
  }
  const message = event.data || {};

  if (message.type === "UNIVERSE_GAME_READY") {
    iframeReady.value = true;
    flushInitToGame();
    return;
  }

  if (message.type === "UNIVERSE_REQUEST_WALLET") {
    try {
      const walletRes = await getWallet({ currencyCode: "GOLD" });
      postToGameFrame({
        type: "UNIVERSE_WALLET_UPDATE",
        payload: {
          balance: walletRes?.data?.balance ?? 0,
          currencyCode: walletRes?.data?.currencyCode || "GOLD",
        },
      });
    } catch {
      postToGameFrame({
        type: "UNIVERSE_WALLET_UPDATE",
        payload: { error: true },
      });
    }
    return;
  }

  if (message.type === "UNIVERSE_REQUEST_LEADERBOARD") {
    try {
      const res = await getLeaderboard(record.value.id);
      postToGameFrame({
        type: "UNIVERSE_LEADERBOARD",
        payload: { list: res?.data ?? [] },
      });
    } catch {
      postToGameFrame({
        type: "UNIVERSE_LEADERBOARD",
        payload: { list: [], error: true },
      });
    }
    return;
  }

  if (message.type === "UNIVERSE_MATCH_RESTART") {
    await refreshInitPayload();
    return;
  }

  if (message.type !== "UNIVERSE_MATCH_SETTLE") return;
  try {
    const settleRes = await settleMatch({
      matchId: currentMatchId.value || message.payload?.matchId || "",
      gameId: record.value.id,
      score: Number(message.payload?.score || 0),
      durationMs: Number(message.payload?.durationMs || 0),
    });
    postToGameFrame({
      type: "UNIVERSE_MATCH_SETTLE_RESULT",
      payload: settleRes?.data || {},
    });
    ElMessage.success("结算成功，奖励已发放");
  } catch (error) {
    postToGameFrame({
      type: "UNIVERSE_MATCH_SETTLE_RESULT",
      payload: { success: false, message: "结算失败" },
    });
    ElMessage.error("结算失败，请稍后重试");
    console.error(error);
  }
};

onMounted(() => {
  window.addEventListener("message", onGameMessage);
});

onBeforeUnmount(() => {
  window.removeEventListener("message", onGameMessage);
});
</script>

<style scoped lang="scss">
@import "@/style/rx";

.game-container {
  padding: 120px 300px 0 230px;

  .game-title {
    height: 94px;
    opacity: 1;
    cursor: pointer;
    font-family: $font_HanSans;
    font-size: 36px;
    font-weight: bold;
    line-height: 94px;
    text-align: center;
    display: flex;
    align-items: center;
    letter-spacing: 0;

    color: rgba(0, 149, 255, 0.4392);
  }

  .game-detail {
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    margin-bottom: 50px;

    .image-list {
      width: 120px;
      margin: 20px 70px 0 50px;

      .image {
        width: 120px;
        height: 120px;
        border-radius: 24px;
        opacity: 1;
        margin-bottom: 15px;
        cursor: pointer;
      }
    }

    .big-image-container {
      margin-right: 70px;

      .big-image {
        left: 467px;
        top: 242px;
        width: 606px;
        height: 564px;
        border-radius: 24px;
        opacity: 1;
      }
    }

    .game-text-container {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      margin-top: 40px;
      height: 510px;

      .text {
        .title {
          opacity: 0.8;
          font-family: $font_HanSans;
          font-size: 24px;
          font-weight: bold;
          line-height: 45px;
          letter-spacing: 4px;
          color: var(--ux-text-primary);
        }

        .type {
          margin-top: 16px;
          opacity: 0.5;
          font-family: $font_HanSans;
          font-size: 16px;
          font-weight: normal;
          line-height: 18.72px;
          display: flex;
          align-items: center;
          letter-spacing: 0;
          color: var(--ux-text-primary);
        }

        .labels {
          margin-top: 16px;
          display: flex;
          justify-content: flex-start;
          align-items: center;

          .label {
            margin-right: 15px;
            font-size: 16px;
            font-family: $font_HanSans;
            border-radius: 10px;
          }
        }

        .description {
          margin-top: 30px;
          opacity: 0.5;
          font-family: $font_HanSans;
          font-size: 16px;
          font-weight: normal;
          line-height: 20px;
          letter-spacing: 0;
          color: var(--ux-text-primary);
        }
      }

      .button {
        width: 207px;
        height: 65px;
        border-radius: 50px;
        font-family: $font_HanSans;
        font-weight: 400;
        font-size: 20px;
        margin-right: 40px;
        font-style: normal;
        text-transform: none;
        background: rgba(255, 255, 255, 0.2);
        border: 8px solid rgba(0, 149, 255, 0.4392);
      }
    }
  }
}

.dialog-game-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding-right: 8px;
}

.dialog-game-title {
  font-family: $font_HanSans;
  font-size: 17px;
  font-weight: 600;
  color: var(--ux-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dialog-game-actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.play-frame-shell {
  position: relative;
  width: 100%;
  height: min(90vh, 820px);
  min-height: 560px;
  overflow: hidden;
  border-radius: 8px;
  background: #111;
}

.play-frame {
  display: block;
  width: 100%;
  height: 100%;
  border: none;
  background: #111;
}

:deep(.game-play-dialog.el-dialog) {
  padding: 0;
  overflow: hidden;
  margin-top: 2vh !important;
  margin-bottom: 2vh !important;
}

:deep(.game-play-dialog .el-dialog__header) {
  margin: 0;
  padding: 12px 16px;
  border-bottom: 1px solid var(--el-border-color-lighter);
}

:deep(.game-play-dialog .el-dialog__body) {
  padding: 8px 12px 12px;
  overflow: hidden;
}
</style>
